import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useBrand } from "@/contexts/BrandContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const brand = useBrand();
  const [email, setEmail]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]       = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (res.status === 429) {
        setError("Too many requests. Please wait a few minutes before trying again.");
        return;
      }
      // Always show success — enumeration-safe
      setSubmitted(true);
    } catch {
      setError("Unable to reach the server. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F3F3F3] px-4">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
      <div className="relative w-full max-w-md">
        {/* Brand header */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded bg-[#0176D3] flex items-center justify-center mb-4">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <h1 data-testid="brand-product-name" className="text-3xl font-bold text-[#181818] font-sans tracking-tight">
            {brand?.productName}
          </h1>
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-lg p-8" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          {!submitted ? (
            <>
              <h2 className="text-lg font-semibold text-[#181818] mb-1">Reset your password</h2>
              <p className="text-sm text-[#706E6B] mb-6">
                Enter the email address on your account. If it exists, we'll send a reset link.
              </p>

              <form data-testid="forgot-password-form" onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-[#444] mb-1.5">Email address</label>
                  <input
                    data-testid="forgot-password-email-input"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    placeholder="you@company.com"
                    className="w-full px-3 py-2.5 border border-[#D9D9D9] rounded-lg text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] bg-white"
                  />
                </div>

                {error && (
                  <div data-testid="forgot-password-error" className="flex items-center gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-600 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  data-testid="forgot-password-submit-btn"
                  type="submit"
                  disabled={loading || !email}
                  className="w-full py-2.5 px-4 rounded-lg text-sm font-medium bg-[#0176D3] text-white hover:bg-[#014486] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-password-success" className="text-center py-4">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-4" />
              <h3 className="text-base font-semibold text-[#181818] mb-2">Check your email</h3>
              <p className="text-sm text-[#706E6B] leading-relaxed">
                If <strong>{email}</strong> is registered, you'll receive a password reset link within a few minutes.
                Check your spam folder if it doesn't arrive.
              </p>
              <p className="text-xs text-[#999] mt-4">The link expires in 30 minutes.</p>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            data-testid="forgot-password-back-to-login"
            className="inline-flex items-center gap-1.5 text-sm text-[#0176D3] hover:text-[#014486] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
